/**
 * new-flight-words.ts — the sentences a reason a flight cannot go is said in,
 * each as an order block (a key and its values): the airline not in a city,
 * the route outside its network, no flight slot free at an end, a sub-fleet
 * short of the far end, an end too small for it, all of it flying, and the
 * most flights a day a route allows an aircraft.
 *
 * The route builder says them (use-verdict-words.ts), a flight's page says its
 * most (flight-aircraft.tsx), and a refusal from the server is said in them by
 * its code (readSegmentRefusal, segments.ts). Built once, here, so a refusal
 * reads as the builder and the page read, whatever a sentence comes to need.
 * Names are in the player's language as each is built — or, where a city is
 * named by its airport's code (the drag readout), its code.
 */

import { Airline4XRules } from '../constants/server-constants';
import { cityCode, cityName } from '../data/cities';
import i18n from '../i18n/config';
import { formatDecimal } from '../utils/format';
import { airportLevelKey, CITY_BASE_INFLUENCE } from './airports';
import type { OrderBlock } from './order-block';

/** How a sentence names a city: by its name in the player's language, or by its airport's code. */
export type CityNaming = 'name' | 'code';

/** A city's name in the player's language — or its code — by its airport id; nothing for a city not chosen yet. */
export function nameCity(id: number | null, naming: CityNaming = 'name'): string {
  if (id === null) return '';
  return naming === 'code' ? cityCode(id) : cityName(cityCode(id));
}

/** The airline not in a city yet: invest there to fly there. */
export function investToFly(cityId: number, naming: CityNaming = 'name'): OrderBlock {
  return { key: 'globe.drag.investToFly', values: { city: nameCity(cityId, naming) } };
}

/** A route beyond the airline's network: how far from its HQ and its Bases a new route may go. */
export function outsideNetworkNote(): OrderBlock {
  return { key: 'globe.builder.outsideNetworkNote', values: { legs: Airline4XRules.network_hops, influence: CITY_BASE_INFLUENCE } };
}

/** Every flight slot of the team's holding an aircraft at these ends of a route: one end by name, or both. */
export function noSlotNote(endIds: readonly number[], naming: CityNaming = 'name'): OrderBlock {
  return endIds.length > 1
    ? { key: 'globe.builder.noSlotEitherNote' }
    : { key: 'globe.builder.noSlotAtNote', values: { city: nameCity(endIds[0], naming) } };
}

/** How far a route's far end lies past an aircraft's range, in whole kilometres. */
export function kmShort(distanceKm: number, rangeKm: number): number {
  return Math.ceil(distanceKm - rangeKm);
}

/** A sub-fleet `short` kilometres short of the destination. */
export function rangeShort(short: number, destId: number | null, naming: CityNaming = 'name'): OrderBlock {
  return { key: 'globe.builder.restRange', values: { short: formatDecimal(short), city: nameCity(destId, naming) } };
}

/**
 * The end of a route whose airport decides which aircraft land there: the
 * smaller, or the destination when the two are the same size.
 */
export function smallerEndOf(originId: number, destId: number, levelOf: (id: number) => number): number {
  return levelOf(originId) < levelOf(destId) ? originId : destId;
}

/** An end too small for a sub-fleet: the airport level its aircraft need, and the end's own. */
export function airportTooSmall(levelNeeded: number, endId: number | null, endLevel: number, naming: CityNaming = 'name'): OrderBlock {
  return {
    key: 'globe.builder.restAirport',
    values: { level: i18n.t(airportLevelKey(levelNeeded)), city: nameCity(endId, naming), current: formatDecimal(endLevel) },
  };
}

/** Every one of a sub-fleet's `total` aircraft flying. */
export function allFlying(total: number): OrderBlock {
  return { key: 'globe.builder.restFlying', values: { count: total } };
}

/**
 * The most flights a day a route allows an aircraft, as a flight's page says it
 * at its most: by the sub-fleet's name when it is known, else by the route alone.
 */
export function mostADay(most: number, route: string, aircraft: string | null): OrderBlock {
  return aircraft === null
    ? { key: 'globe.flight.mostADayRoute', values: { figure: formatDecimal(most), route } }
    : { key: 'globe.flight.mostADay', values: { figure: formatDecimal(most), aircraft, route } };
}
